import fabric from '@/lib/fabric';
import type { ShapeRuntimeTool } from '@canvas/shared-types';

export interface ShapeStyle {
  fill: string;
  opacity: number;
  strokeWidth: number;
  /** Stroke color; defaults to fill when omitted. */
  stroke?: string;
  /** Corner radius for rect / roundedRect */
  cornerRadius?: number;
}

export interface ShapeCreateOptions {
  /** Regular polygon sides (3–12). Default 6. */
  sides?: number;
}

/** Plain points — Fabric Polygon accepts {x,y}[]; fabric.Point mid-drag updates are flaky. */
type Pt = { x: number; y: number };

/** Unit polygon diameter used for scale-based drag (radius 50 → ~100×100). */
const POLY_BASE_RADIUS = 50;
const POLY_BASE_SIZE = POLY_BASE_RADIUS * 2;

function starPoints(spikes: number, outer: number, inner: number): Pt[] {
  const points: Pt[] = [];
  let rot = (Math.PI / 2) * 3;
  const step = Math.PI / spikes;

  for (let i = 0; i < spikes; i++) {
    points.push({ x: Math.cos(rot) * outer, y: Math.sin(rot) * outer });
    rot += step;
    points.push({ x: Math.cos(rot) * inner, y: Math.sin(rot) * inner });
    rot += step;
  }
  return points;
}

/** Regular N-gon centered at origin, point-top. */
export function regularPolygonPoints(sides: number, radius: number): Pt[] {
  const n = Math.max(3, Math.min(12, Math.round(sides)));
  const points: Pt[] = [];
  const offset = -Math.PI / 2;
  for (let i = 0; i < n; i += 1) {
    const a = offset + (i * 2 * Math.PI) / n;
    points.push({ x: Math.cos(a) * radius, y: Math.sin(a) * radius });
  }
  return points;
}

const common = {
  selectable: false,
  evented: false,
  originX: 'left' as const,
  originY: 'top' as const,
};

function fillStroke(style: ShapeStyle) {
  const sw = Math.max(0, style.strokeWidth || 0);
  return {
    fill: style.fill,
    opacity: style.opacity,
    stroke: sw > 0 ? style.stroke || style.fill : undefined,
    strokeWidth: sw,
  };
}

/** Create a zero-size (or point) shape at the drag origin. */
export function createDragShape(
  tool: ShapeRuntimeTool,
  origin: { x: number; y: number },
  style: ShapeStyle,
  options?: ShapeCreateOptions
): fabric.Object {
  const sides = options?.sides ?? 6;
  const paint = fillStroke(style);
  const radius = style.cornerRadius ?? (tool === 'roundedRect' ? 16 : 0);

  switch (tool) {
    case 'rectangle':
    case 'roundedRect':
      return new fabric.Rect({
        left: origin.x,
        top: origin.y,
        width: 1,
        height: 1,
        rx: radius,
        ry: radius,
        objectCaching: false,
        ...paint,
        ...common,
      });
    case 'circle':
      return new fabric.Ellipse({
        left: origin.x,
        top: origin.y,
        rx: 0.5,
        ry: 0.5,
        objectCaching: false,
        ...paint,
        ...common,
      });
    case 'triangle':
      return new fabric.Triangle({
        left: origin.x,
        top: origin.y,
        width: 1,
        height: 1,
        objectCaching: false,
        ...paint,
        ...common,
      });
    case 'polygon':
      // Fixed geometry + scale during drag (mutating points breaks Fabric pathOffset)
      return new fabric.Polygon(regularPolygonPoints(sides, POLY_BASE_RADIUS), {
        left: origin.x,
        top: origin.y,
        scaleX: 0.01,
        scaleY: 0.01,
        objectCaching: false,
        ...paint,
        ...common,
      });
    case 'star':
      return new fabric.Polygon(
        starPoints(5, POLY_BASE_RADIUS, POLY_BASE_RADIUS * 0.45),
        {
          left: origin.x,
          top: origin.y,
          scaleX: 0.01,
          scaleY: 0.01,
          objectCaching: false,
          ...paint,
          ...common,
        }
      );
    case 'line':
    case 'arrow':
      return new fabric.Line([origin.x, origin.y, origin.x, origin.y], {
        stroke: style.stroke || style.fill,
        strokeWidth: Math.max(1, style.strokeWidth),
        opacity: style.opacity,
        selectable: false,
        evented: false,
        strokeLineCap: 'round',
      });
    default:
      return new fabric.Rect({
        left: origin.x,
        top: origin.y,
        width: 0,
        height: 0,
        ...paint,
        ...common,
      });
  }
}

/** Resize / reshape during drag. */
export function updateDragShape(
  tool: ShapeRuntimeTool,
  shape: fabric.Object,
  origin: { x: number; y: number },
  point: { x: number; y: number },
  options?: ShapeCreateOptions
): void {
  const left = Math.min(origin.x, point.x);
  const top = Math.min(origin.y, point.y);
  const w = Math.abs(point.x - origin.x);
  const h = Math.abs(point.y - origin.y);

  switch (tool) {
    case 'rectangle':
    case 'roundedRect':
      shape.set({
        left,
        top,
        width: Math.max(1, w),
        height: Math.max(1, h),
        scaleX: 1,
        scaleY: 1,
      });
      break;
    case 'circle':
      (shape as fabric.Ellipse).set({
        left,
        top,
        rx: Math.max(0.5, w / 2),
        ry: Math.max(0.5, h / 2),
        scaleX: 1,
        scaleY: 1,
      });
      break;
    case 'triangle':
      shape.set({
        left,
        top,
        width: Math.max(1, w),
        height: Math.max(1, h),
        scaleX: 1,
        scaleY: 1,
      });
      break;
    case 'polygon':
    case 'star': {
      const size = Math.max(w, h, 1);
      const scale = size / POLY_BASE_SIZE;
      shape.set({
        left,
        top,
        scaleX: scale,
        scaleY: scale,
      });
      break;
    }
    case 'line':
    case 'arrow':
      (shape as fabric.Line).set({
        x1: origin.x,
        y1: origin.y,
        x2: point.x,
        y2: point.y,
      });
      break;
  }

  // Required so mouseup size checks / hit targets see the dragged bounds.
  // Line/arrow already worked because they called this; other shapes did not.
  shape.setCoords();
  shape.dirty = true;
}

function lineEndpoints(line: fabric.Line): { x1: number; y1: number; x2: number; y2: number } {
  const local = line.calcLinePoints();
  const matrix = line.calcTransformMatrix();
  const p1 = fabric.util.transformPoint(
    new fabric.Point(local.x1, local.y1),
    matrix
  );
  const p2 = fabric.util.transformPoint(
    new fabric.Point(local.x2, local.y2),
    matrix
  );
  return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
}

/** Convert a dragged line into a line + arrowhead group. */
export function finalizeArrow(
  canvas: fabric.Canvas,
  line: fabric.Line,
  style: ShapeStyle
): fabric.Object {
  const { x1, y1, x2, y2 } = lineEndpoints(line);
  const stroke = (line.stroke as string) || style.fill;
  const strokeWidth = line.strokeWidth || style.strokeWidth || 3;
  const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
  const head = Math.max(12, strokeWidth * 4);

  const shaft = new fabric.Line([x1, y1, x2, y2], {
    stroke,
    strokeWidth,
    opacity: style.opacity,
    strokeLineCap: 'round',
  });

  const tip = new fabric.Triangle({
    left: x2,
    top: y2,
    originX: 'center',
    originY: 'center',
    angle: angle + 90,
    width: head * 0.9,
    height: head,
    fill: stroke,
    opacity: style.opacity,
  });

  canvas.remove(line);
  const group = new fabric.Group([shaft, tip], {
    selectable: true,
    evented: true,
  });
  canvas.add(group);
  return group;
}

export function isLinearTool(tool: ShapeRuntimeTool): boolean {
  return tool === 'line' || tool === 'arrow';
}

/**
 * Replace a scale-dragged polygon/star with a scale=1 polygon of the same
 * visual size (Fabric hit-testing / export are more reliable this way).
 */
export function finalizeDragPolygon(
  canvas: fabric.Canvas,
  poly: fabric.Polygon,
  tool: 'polygon' | 'star',
  options?: ShapeCreateOptions
): fabric.Polygon {
  const size = Math.max(
    (poly.width || POLY_BASE_SIZE) * Math.abs(poly.scaleX || 1),
    (poly.height || POLY_BASE_SIZE) * Math.abs(poly.scaleY || 1),
    4
  );
  const left = poly.left || 0;
  const top = poly.top || 0;
  const sides = options?.sides ?? 6;
  const points =
    tool === 'star'
      ? starPoints(5, size / 2, size * 0.225)
      : regularPolygonPoints(sides, size / 2);

  const next = new fabric.Polygon(points, {
    left,
    top,
    fill: poly.fill,
    stroke: poly.stroke,
    strokeWidth: poly.strokeWidth,
    opacity: poly.opacity,
    originX: 'left',
    originY: 'top',
    selectable: true,
    evented: true,
    objectCaching: true,
  });

  canvas.remove(poly);
  canvas.add(next);
  return next;
}
